import { createPopper, type Instance, type Placement } from '@popperjs/core';
import {
  cloneElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type TooltipPlacement =
  | Exclude<Placement, 'auto' | 'auto-start' | 'auto-end'>
  | 'auto';

export interface TooltipProps {
  title: ReactNode;
  children: ReactElement<{ id?: string; 'aria-describedby'?: string }>;
  placement?: TooltipPlacement;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  id?: string;
  'data-cy'?: string;
}

const SHOW_DELAY = 200;
const HIDE_DELAY = 50;

const hasTooltipContent = (title: ReactNode): boolean =>
  title !== null && title !== undefined && title !== false && title !== '';

const placementClass = (placement: Placement): string =>
  `bs-tooltip-${placement.split('-')[0]}`;

const Tooltip = ({
  title,
  children,
  placement = 'top',
  open,
  onOpen,
  onClose,
  id,
  'data-cy': dataCy,
}: TooltipProps): ReactElement => {
  const reactId = useId().replace(/:/g, '');
  const targetId = children.props.id ?? `tooltip-target-${reactId}`;
  const tooltipId = id ?? `tooltip-${reactId}`;
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] =
    useState<Placement>(placement);
  const [isPositioned, setIsPositioned] = useState(false);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<Instance>();
  const hasContent = hasTooltipContent(title);
  const isOpen = hasContent && (isControlled ? Boolean(open) : internalOpen);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== undefined) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  }, []);

  const clearShowTimeout = useCallback(() => {
    if (showTimeoutRef.current !== undefined) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }
  }, []);

  const show = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    if (!hasContent || isOpen) return;

    if (!isControlled) setInternalOpen(true);
    onOpen?.();
  }, [
    clearHideTimeout,
    clearShowTimeout,
    hasContent,
    isControlled,
    isOpen,
    onOpen,
  ]);

  const showWithDelay = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    if (!hasContent || isOpen) return;

    if (isControlled) {
      show();
      return;
    }

    showTimeoutRef.current = setTimeout(show, SHOW_DELAY);
  }, [
    clearHideTimeout,
    clearShowTimeout,
    hasContent,
    isControlled,
    isOpen,
    show,
  ]);

  const hide = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    if (!isOpen) return;

    if (!isControlled) setInternalOpen(false);
    onClose?.();
  }, [clearHideTimeout, clearShowTimeout, isControlled, isOpen, onClose]);

  const hideWithDelay = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(hide, HIDE_DELAY);
  }, [clearHideTimeout, clearShowTimeout, hide]);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return undefined;

    const movedWithinTarget = (event: MouseEvent) =>
      event.relatedTarget instanceof Node &&
      target.contains(event.relatedTarget);
    const handleMouseOver = (event: MouseEvent) => {
      if (!movedWithinTarget(event)) showWithDelay();
    };
    const handleMouseOut = (event: MouseEvent) => {
      if (!movedWithinTarget(event)) hideWithDelay();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    target.addEventListener('mouseover', handleMouseOver);
    target.addEventListener('mouseout', handleMouseOut);
    target.addEventListener('focusin', show);
    target.addEventListener('focusout', hide);
    target.addEventListener('keydown', handleKeyDown);

    return () => {
      target.removeEventListener('mouseover', handleMouseOver);
      target.removeEventListener('mouseout', handleMouseOut);
      target.removeEventListener('focusin', show);
      target.removeEventListener('focusout', hide);
      target.removeEventListener('keydown', handleKeyDown);
    };
  }, [hide, hideWithDelay, show, showWithDelay, targetId]);

  useEffect(() => {
    if (hasContent) return;

    clearShowTimeout();
    clearHideTimeout();
    if (!isControlled) setInternalOpen(false);
  }, [clearHideTimeout, clearShowTimeout, hasContent, isControlled]);

  useEffect(
    () => () => {
      clearShowTimeout();
      clearHideTimeout();
    },
    [clearHideTimeout, clearShowTimeout]
  );

  useLayoutEffect(() => {
    if (!isOpen || !tooltipRef.current) return undefined;

    const target = document.getElementById(targetId);
    if (!target) return undefined;

    setIsPositioned(false);
    popperRef.current = createPopper(target, tooltipRef.current, {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: { offset: [0, 8] },
        },
        { name: 'flip', enabled: true },
        { name: 'preventOverflow', enabled: true },
        {
          name: 'arrow',
          options: { element: arrowRef.current },
        },
        {
          name: 'syncPlacement',
          enabled: true,
          phase: 'afterWrite',
          fn: ({ state }) => {
            setResolvedPlacement(state.placement);
            setIsPositioned(true);
          },
        },
      ],
    });

    return () => {
      popperRef.current?.destroy();
      popperRef.current = undefined;
    };
  }, [isOpen, placement, targetId]);

  useEffect(() => {
    void popperRef.current?.update();
  }, [resolvedPlacement, title]);

  const describedBy = isOpen
    ? [children.props['aria-describedby'], tooltipId].filter(Boolean).join(' ')
    : children.props['aria-describedby'];
  const target = cloneElement(children, {
    id: targetId,
    'aria-describedby': describedBy,
  });

  return (
    <>
      {target}
      {isOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`tooltip fade show ${placementClass(resolvedPlacement)}`}
            style={{
              pointerEvents: 'none',
              visibility: isPositioned ? 'visible' : 'hidden',
            }}
          >
            <div ref={arrowRef} className="arrow" data-popper-arrow />
            <div
              id={tooltipId}
              className="tooltip-inner"
              role="tooltip"
              data-cy={dataCy}
            >
              {title}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
